import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { LoginService } from '../components/login/login.service';
import { environment } from '../../environments/environment';

/** localStorage keys shared across tabs for session timeout coordination. */
export const SESSION_STORAGE_KEYS = {
  LOGGED_IN: 'loggedIn',
  TIMER_STARTED: 'timerStarted',
  TIMER_CLOSE_TIME: 'timerCloseTime',
  LAST_ACTIVITY: 'lastUserActivity',
  SESSION_LOGOUT: 'sessionLogout',
  LOGIN_ID: 'loginId',
  LOGIN_REPORT_ID: 'loginReportId',
  BEARER_TOKEN: 'bearerToken',
};

/**
 * Coordinates inactivity timeout, backend login-report closure, and multi-tab sync.
 *
 * Timeout flow (production):
 * 1. @ng-idle detects no user input for 3600s (1 hour).
 * 2. A shared 60s countdown starts (one interval across tabs via timerStarted).
 * 3. performLogout() POSTs /logout with loginReportId, then Auth0 logout.
 *
 * Multi-tab: activity in any tab updates lastUserActivity; other tabs receive a
 * storage event and reset their idle timers so one active tab cannot be overridden
 * by an idle tab's countdown. Logout in one tab sets sessionLogout so all tabs stop.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private logoutInProgress = false;
  /** Prevents writing localStorage on every mousemove — max one broadcast per interval. */
  private lastActivityBroadcastAt = 0;
  private readonly activityBroadcastMs = 3000;

  /** Idle period before the auto-logout warning (seconds). */
  get idleSeconds(): number {
    return environment.DEVELOPMENTTITLE === '' ? 3600 : 604800;
  }

  readonly countdownSeconds = 60;

  isLogoutInProgress(): boolean {
    return this.logoutInProgress;
  }

  /**
   * Broadcasts activity to other tabs (via storage event). Throttled so five open
   * tabs do not ping-pong localStorage writes and peg CPU.
   * Returns true when a write occurred.
   */
  recordActivity(): boolean {
    const now = Date.now();
    if (now - this.lastActivityBroadcastAt < this.activityBroadcastMs) {
      return false;
    }
    this.lastActivityBroadcastAt = now;
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.LAST_ACTIVITY, String(now));
    return true;
  }

  clearCountdown(): void {
    window?.localStorage?.removeItem(SESSION_STORAGE_KEYS.TIMER_CLOSE_TIME);
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.TIMER_STARTED, 'false');
  }

  /** Starts the shared logout countdown timestamp (read by all tabs for the dialog). */
  startCountdown(): void {
    const timerCloseTime = Date.now() + this.countdownSeconds * 1000;
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.TIMER_CLOSE_TIME, String(timerCloseTime));
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.TIMER_STARTED, 'true');
  }

  isCountdownRunning(): boolean {
      try {
          return window?.localStorage?.getItem(
              SESSION_STORAGE_KEYS.TIMER_STARTED
          ) === 'true';
      } catch {
          return false;
      }
  }

  /** Remaining seconds on the shared countdown, or null when no countdown is active. */
  getCountdownSeconds(): number | null {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    try {
      const timerCloseTimeString = window?.localStorage?.getItem(SESSION_STORAGE_KEYS.TIMER_CLOSE_TIME);
      if (!timerCloseTimeString) {
        return null;
      }

      const timerCloseTime = Number(timerCloseTimeString);
      if (Number.isNaN(timerCloseTime)) {
        return null;
      }

      const diffInSeconds = (timerCloseTime - Date.now()) / 1000;
      return diffInSeconds >= 0 ? Math.round(diffInSeconds) : 0;
    } catch (error) {
      console.warn('Error reading session countdown from localStorage:', error);
      return null;
    }
  }

  isCountdownExpired(): boolean {
    const seconds = this.getCountdownSeconds();
    return seconds !== null && seconds <= 0;
  }

  /**
   * Ends the session: stops countdown, notifies other tabs, closes the backend
   * login report (session duration tracking), then signs out via Auth0.
   * Safe to call from auto-timeout, manual logout, or idle expiry handlers.
   */
  performLogout(loginService: LoginService, authService: AuthService): void {
    if (this.logoutInProgress) {
      return;
    }
    this.logoutInProgress = true;

    const loginId = window?.localStorage?.getItem(SESSION_STORAGE_KEYS.LOGIN_ID);
    const loginReportId = window?.localStorage?.getItem(SESSION_STORAGE_KEYS.LOGIN_REPORT_ID);
    const token = window?.localStorage?.getItem(SESSION_STORAGE_KEYS.BEARER_TOKEN);

    this.clearCountdown();
    // Other tabs listen for sessionLogout and redirect without starting new tracking.
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.SESSION_LOGOUT, String(Date.now()));
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.LOGGED_IN, 'false');

    const finishAuthLogout = () => {
      authService.logout({ logoutParams: { returnTo: document.location.origin }} as any);
    };

    if (!token) {
      window?.localStorage?.clear();
      window?.localStorage?.setItem(SESSION_STORAGE_KEYS.LOGGED_IN, 'false');
      finishAuthLogout();
      return;
    }

    const logoutData = { loginId, loginReportId };
    loginService.logout(logoutData, token).subscribe({
      next: () => finishAuthLogout(),
      error: () => {
        window?.localStorage?.clear();
        window?.localStorage?.setItem(SESSION_STORAGE_KEYS.LOGGED_IN, 'false');
        finishAuthLogout();
      },
    });
  }

  /**
   * Best-effort backend logout when the page is unloading during an active
   * auto-logout countdown (fetch keepalive survives tab close better than XHR).
   */
  sendLogoutKeepalive(): void {
    if (this.logoutInProgress) {
      return;
    }

    const token = window?.localStorage?.getItem(SESSION_STORAGE_KEYS.BEARER_TOKEN);
    const loginId = window?.localStorage?.getItem(SESSION_STORAGE_KEYS.LOGIN_ID);
    const loginReportId = window?.localStorage?.getItem(SESSION_STORAGE_KEYS.LOGIN_REPORT_ID);

    if (!token || !loginReportId) {
      return;
    }

    this.logoutInProgress = true;
    this.clearCountdown();
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.SESSION_LOGOUT, String(Date.now()));
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.LOGGED_IN, 'false');

    fetch(`${environment.apiUrl}/logout`, {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ loginId, loginReportId }),
      keepalive: true,
    }).catch(() => undefined);
  }
}
